import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableCell } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import lodash from "lodash";

// COMPONENTS
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import StatusUserComponent from "@/main/components/shared/status/statusUserComponent";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import DropdownButton from "@/main/components/shared/dropdownButton";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { KOGTableRow } from "@/main/components/shared/boxGrid/styles";

// SERVICES
import { userApi } from "@/main/api";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { UserFilterRequest, UserStatusFilterRequest } from "@/main/models/request";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { UserSearchGridResponse } from "@/main/models/response";
import { getOptionsStatusUser } from "@/main/services/data/data";
import { getDefaultMessageResultsFound, getTranslateByKey, haveIgnoreElement } from "@/main/services/utils";

import { StatusUserEnum } from "@/main/services/enum/StatusUserEnum";
import * as S from "./styles";
import { toast } from "react-toastify";

export const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};
export interface IGridData {
	data: UserSearchGridResponse[];
	totalItems: number;
	isLoading: boolean;
}

interface IUserSearchGridProps {
	gridData: IGridData;
	formFilters: UserFilterRequest | null;
	setGridData: React.Dispatch<React.SetStateAction<IGridData>>;
}

const SIZE_COLUMNS = ["13%", "responsive", "20%", "20%", "15%", "150px"];

function UserSearchGrid({ gridData, formFilters, setGridData }: IUserSearchGridProps) {
	// Hooks
	const navigate = useNavigate();

	// States
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasShowMore, setHasShowMore] = useState(false);
	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
	const optionsStatusUser = getOptionsStatusUser();
	const mockedBlockedOption = {
		id: StatusUserEnum.Blocked,
		name: getTranslateByKey("statusUser.blocked"),
	};

	// Handle Functions
	function handleRowBoxGrid(user: UserSearchGridResponse, event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) {
		if (haveIgnoreElement(event.target as HTMLElement)) return;
		if (user.hasOtherCompanyRelation) {
			switch (user.companyType) {
				case getTranslateByKey("companyType.bodyshop"):
					toast.error(getTranslateByKey("information.bodyshopAssociatedWithAnotherInsurance", true));
					break;

				case getTranslateByKey("companyType.valuator"):
					toast.error(getTranslateByKey("information.valuatorAssociatedWithAnotherInsurance", true));
					break;

				case getTranslateByKey("companyType.supervisor"):
					toast.error(getTranslateByKey("information.supervisorAssociatedWithAnotherInsurance", true));
					break;

				case getTranslateByKey("companyType.supplier"):
					toast.error(getTranslateByKey("information.supplierAssociatedWithAnotherInsurance", true));
					break;

				default:
					toast.error(getTranslateByKey("information.companyAssociatedWithAnotherInsurance", true));
					break;
			}
			return;
		}
		navigate(`${RoutesConstants.registrationRegisterUser}?userGuid=${user.guid}`);
	}

	function handleChangeStatus(user: UserSearchGridResponse, option: any) {
		if (user.hasOtherCompanyRelation) {
			switch (user.companyType) {
				case getTranslateByKey("companyType.bodyshop"):
					toast.error(getTranslateByKey("information.bodyshopAssociatedWithAnotherInsurance", true));
					break;

				case getTranslateByKey("companyType.valuator"):
					toast.error(getTranslateByKey("information.valuatorAssociatedWithAnotherInsurance", true));
					break;

				case getTranslateByKey("companyType.supervisor"):
					toast.error(getTranslateByKey("information.supervisorAssociatedWithAnotherInsurance", true));
					break;

				case getTranslateByKey("companyType.supplier"):
					toast.error(getTranslateByKey("information.supplierAssociatedWithAnotherInsurance", true));
					break;

				default:
					toast.error(getTranslateByKey("information.companyAssociatedWithAnotherInsurance", true));
					break;
			}
			return;
		}
		const { locked } = option;
		const { key, value } = option.status;

		if (user.status.name !== value && user.isBlocked === locked) {
			const statusFormatted = { id: key, name: value };

			const request = {
				guid: user.guid,
				idStatusUser: locked === true ? 3 : key,
			} as UserStatusFilterRequest;

			userApi.changeStatusUser(request).then(() =>
				setGridData((prev) => {
					const clonePrev = lodash.cloneDeep(prev.data);
					const index = clonePrev.findIndex((x) => x.guid === user.guid);

					clonePrev[index].status = statusFormatted;
					clonePrev[index].isBlocked = locked;

					return {
						data: clonePrev,
						totalItems: prev.totalItems,
						isLoading: prev.isLoading,
					};
				}),
			);
		}

		if (user.status.name === value && user.isBlocked !== locked) {
			const statusFormatted = { id: key, name: value };

			const request = {
				guid: user.guid,
				isBlocked: locked,
				idStatusUser: locked === true ? 3 : key,
			} as UserStatusFilterRequest;

			userApi.changeStatusUser(request).then(() =>
				setGridData((prev) => {
					const clonePrev = lodash.cloneDeep(prev.data);
					const index = clonePrev.findIndex((x) => x.guid === user.guid);

					clonePrev[index].status = statusFormatted;
					clonePrev[index].isBlocked = locked;

					return {
						data: clonePrev,
						totalItems: prev.totalItems,
						isLoading: prev.isLoading,
					};
				}),
			);
		}

		if (user.status.name !== value && user.isBlocked !== locked) {
			const statusFormatted = { id: key, name: value };

			const request = {
				guid: user.guid,
				isBlocked: locked,
				idStatusUser: locked === true ? 3 : key,
			} as UserStatusFilterRequest;

			userApi.changeStatusUser(request).then(() =>
				setGridData((prev) => {
					const clonePrev = lodash.cloneDeep(prev.data);
					const index = clonePrev.findIndex((x) => x.guid === user.guid);

					clonePrev[index].status = statusFormatted;
					clonePrev[index].isBlocked = locked;

					return {
						data: clonePrev,
						totalItems: prev.totalItems,
						isLoading: prev.isLoading,
					};
				}),
			);
		}
	}

	// ASYNC FUNCTIONS
	function onLoadInfiniteScroll(request: UserFilterRequest) {
		setIsLoadingInfiniteScroll(true);

		userApi
			.getSearchGrid(request)
			.then((response) => {
				let resultData: UserSearchGridResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data && response.data.results && response.data.results.length > 0) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}
				if (gridData.data.length > 0) {
					resultData = [...gridData.data, ...resultData];
				}
				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					data: resultData,
				});

				// Disable show more
				if (resultTotalItems < PaginationEnum.PageSize10 || resultTotalItems === resultData.length) {
					setHasShowMore(false);
				}
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}
	function onLoadSearchGrid() {
		const index = pageIndex + PaginationEnum.PageIndexIntial;
		setPageIndex(index);
		const formFiltersOnLoadSearchGrid = formFilters;
		if (formFiltersOnLoadSearchGrid) {
			const updatedFormFilters = {
				...formFiltersOnLoadSearchGrid,
				pageIndex: index,
			};
			onLoadInfiniteScroll(updatedFormFilters);
		}
	}

	useEffect(() => {
		setHasShowMore(gridData.data.length < gridData.totalItems);
	}, [gridData.data]);

	useEffect(() => {
		setPageIndex(PaginationEnum.PageIndexIntial);
	}, [formFilters]);

	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data.length === 0 && formFilters !== null) return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;

	// CONSTANTS LAYOUT
	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);
	const rowHeaders = [
		getTranslateByKey("information.companyType"),
		getTranslateByKey("information.company"),
		getTranslateByKey("information.name"),
		getTranslateByKey("information.login"),
		getTranslateByKey("information.userGroup"),
		getTranslateByKey("information.status"),
	];
	const isBlockedUser = (user: UserSearchGridResponse) => (user.isBlocked ? mockedBlockedOption : user.status);
	return (
		<S.Container>
			<BoxGrid
				title={titleGrid}
				rowHeaders={rowHeaders}
				sizeColumns={SIZE_COLUMNS}
				infiniteScrollConfig={{
					dataLength: gridData.data.length,
					fetchData: () => onLoadSearchGrid(),
					hasMore: hasShowMore,
					isLoading: isLoadingInfiniteScroll,
				}}
			>
				{gridData.data.map((user) => (
					<KOGTableRow
						key={user.guid}
						onClick={(e) => handleRowBoxGrid(user, e)}
						id={`id-searchUser-tableRow-result-${user.name}`}
					>
						<S.EllipsisCell title={user.companyType}>{user.companyType}</S.EllipsisCell>
						<S.EllipsisCell title={user.company}>{user.company}</S.EllipsisCell>
						<S.EllipsisCell title={user.name}>{user.name}</S.EllipsisCell>
						<S.EllipsisCell title={user.login}>{user.login}</S.EllipsisCell>
						<S.EllipsisCell title={user.userGroup}>{user.userGroup}</S.EllipsisCell>
						<TableCell>
							<S.AreaStatus>
								<StatusUserComponent status={isBlockedUser(user)} />
								<DropdownButton
									trigger={<MoreVertIcon id={`id-searchUser-btn-openDrop-${user.name}`} className="ignore-click" />}
									options={optionsStatusUser}
									status={user}
									isGroup={false}
									disabled={user.hasOtherCompanyRelation}
									onClick={(option) => handleChangeStatus(user, option)}
								/>
							</S.AreaStatus>
						</TableCell>
					</KOGTableRow>
				))}
			</BoxGrid>
		</S.Container>
	);
}

export default UserSearchGrid;
